// Copyright (c) StrangeBee 2023

import { DisplayEmptyData } from '@Atoms/Display/DisplayEmptyData/DisplayEmptyData';
import { TitleSection } from '@Atoms/TitleSection/TitleSection';
import { useLocalMessage } from '@Helpers/locale';
import { CFValueItemAdd } from '@Molecules/CFValueItemAdd/CFValueItemAdd';
import { THSelect } from '@Molecules/Inputs/THSelect/THSelect';
import { THForm } from '@Molecules/THForm/THForm';
import { Form, FormInstance, Tabs } from 'antd';
import { FormListFieldData } from 'antd/lib/form/FormList';
import React, { memo, ReactElement, ReactNode, useCallback, useMemo, useState } from 'react';
import { CaseCustomField, CustomFieldValueOutput, CustomFieldOutput } from 'thehive-sdk';
import { CFListItem } from './CFListItem';
import { flatten } from 'lodash';

const { TabPane } = Tabs;

import './CFList.scss';

interface CFListProps {
    customFields: CustomFieldOutput[];
    borderless?: boolean;
    disabled?: boolean;
    label?: ReactNode;
    isDeletable?: boolean;
    form: FormInstance;
    activeKeyState: {
        activeKey: string;
        setActiveKey: React.Dispatch<React.SetStateAction<string>>;
    };
}

export const CFList = memo(
    ({
        customFields,
        borderless,
        disabled = false,
        label,
        isDeletable = true,
        form,
        activeKeyState,
    }: CFListProps): ReactElement | null => {
        const i = useLocalMessage();
        const [itemsPerRow, setItemsPerRow] = useState<1 | 2 | 3>(3);
        const { activeKey, setActiveKey } = activeKeyState;

        const customFieldsLength = customFields?.length;

        const caseCustomFieldNative = Form.useWatch<CaseCustomField[]>(['customFields'], form);

        const caseCustomFields = useMemo(
            () =>
                (caseCustomFieldNative || [])?.sort(({ group: groupA = '' }, { group: groupB = '' }) =>
                    groupA?.localeCompare(groupB),
                ) || [],
            [caseCustomFieldNative],
        );

        const onAddCF = useCallback(
            (
                customField: Partial<CaseCustomField>,
                currentCustomFields: Record<
                    string,
                    Record<
                        string,
                        (CustomFieldValueOutput & {
                            field: FormListFieldData;
                        })[]
                    >
                >,
                add: (defaultValue?: any) => void,
            ) => {
                const booleanExisting = !!flatten(
                    Object.values(currentCustomFields).map((value) => flatten(Object.values(value))),
                ).find(({ name, type }) => name === customField.name && type === 'boolean');

                const { description, displayName, group = '', name, type } = customField || {};

                if (!booleanExisting) {
                    add({ description, displayName, group, name, type });
                }

                const groups = [...Object.keys(currentCustomFields), group].sort((groupA = '', groupB = '') =>
                    groupA?.localeCompare(groupB),
                );

                if (group) {
                    const index = groups.indexOf(group);
                    if (index != -1) {
                        setActiveKey(index + '');
                    }
                }
            },
            [setActiveKey],
        );

        return customFieldsLength ? (
            <THForm.Item<{ customFields: CaseCustomField[] }>
                shouldUpdate={(prev, next) => prev.customFields !== next.customFields}
            >
                <THForm.List name="customFields">
                    {(fields, { add, remove }) => {
                        const parsedCustomFields = caseCustomFields.reduce<
                            Record<string, Record<string, (CustomFieldValueOutput & { field: FormListFieldData })[]>>
                        >(
                            (acc, caseCF, index) => {
                                const { name: caseCFName } = caseCF;
                                const customField = customFields.find(({ name }) => caseCFName === name);

                                if (!customField) {
                                    return acc;
                                }

                                const { group = 'default', name = '' } = customField;

                                return {
                                    ...acc,
                                    [group]: {
                                        ...(acc[group] || {}),
                                        [name]: [
                                            ...(acc[group][name] || []),
                                            {
                                                ...caseCF,
                                                value: caseCF?.value,
                                                field: fields[index],
                                            },
                                        ],
                                    },
                                };
                            },
                            customFields.reduce((acc, { group = 'default' }) => ({ ...acc, [group]: {} }), {}),
                        );

                        const currentCustomFields = Object.entries(parsedCustomFields).reduce<
                            Record<string, Record<string, (CustomFieldValueOutput & { field: FormListFieldData })[]>>
                        >(
                            (acc, [group, content]) =>
                                Object.keys(content).length ? { ...acc, [group]: content } : acc,
                            {},
                        );

                        return (
                            <THForm.Item
                                label={
                                    label ? (
                                        <TitleSection
                                            extraAction={
                                                !disabled && (
                                                    <CFValueItemAdd
                                                        className="cf-list__add-cf-button"
                                                        customFieldList={customFields}
                                                        onAdd={(customField) =>
                                                            onAddCF(customField, currentCustomFields, add)
                                                        }
                                                    />
                                                )
                                            }
                                        >
                                            {i('common.custom-fields')}
                                        </TitleSection>
                                    ) : (
                                        i('common.custom-fields')
                                    )
                                }
                                className="cf-list__label__extra-action"
                                extraAction={
                                    !disabled &&
                                    !label && (
                                        <CFValueItemAdd
                                            className="cf-list__add-cf-button"
                                            customFieldList={customFields}
                                            onAdd={(customField) => onAddCF(customField, currentCustomFields, add)}
                                        />
                                    )
                                }
                            >
                                {caseCustomFields.length ? (
                                    <Tabs
                                        className="cf-list__tabs"
                                        activeKey={activeKey}
                                        onChange={(activeKey) => setActiveKey(activeKey)}
                                        tabBarExtraContent={
                                            <div className="cf-list__extra-action">
                                                <THForm.Item noStyle>
                                                    <THSelect
                                                        value={itemsPerRow}
                                                        onChange={(value) => setItemsPerRow(value)}
                                                        options={[1, 2, 3].map((value) => ({ label: value, value }))}
                                                    />
                                                </THForm.Item>
                                            </div>
                                        }
                                    >
                                        {Object.entries(currentCustomFields)
                                            .sort(([groupA], [groupB]) => groupA.localeCompare(groupB))
                                            .map(([group, cfList], index) => {
                                                const cfNumber = Object.entries(cfList).reduce(
                                                    (acc, [, cf]) => acc + Object.values(cf).length,
                                                    0,
                                                );

                                                return (
                                                    <TabPane
                                                        tab={`${group} (${cfNumber})`}
                                                        key={index + ''}
                                                        tabKey={index + ''}
                                                    >
                                                        {!!cfNumber ? (
                                                            <div
                                                                className="cf-list__layout"
                                                                style={{
                                                                    gridTemplateColumns: `repeat(${itemsPerRow}, 1fr)`,
                                                                }}
                                                            >
                                                                {Object.entries(cfList)
                                                                    .sort(([name], [name2]) =>
                                                                        name.localeCompare(name2),
                                                                    )
                                                                    .map(([name, customFields]) => (
                                                                        <CFListItem
                                                                            name={name}
                                                                            customFields={customFields}
                                                                            borderless={borderless}
                                                                            disabled={disabled}
                                                                            key={name}
                                                                            onAdd={(customField) =>
                                                                                onAddCF(
                                                                                    customField,
                                                                                    currentCustomFields,
                                                                                    add,
                                                                                )
                                                                            }
                                                                            onDelete={
                                                                                isDeletable
                                                                                    ? (fieldName) => {
                                                                                          remove(fieldName);

                                                                                          if (cfNumber === 1) {
                                                                                              setActiveKey('0');
                                                                                          }
                                                                                      }
                                                                                    : undefined
                                                                            }
                                                                        />
                                                                    ))}
                                                            </div>
                                                        ) : (
                                                            <DisplayEmptyData
                                                                label={
                                                                    <>
                                                                        {i('customField.none-group')}
                                                                        {!disabled && (
                                                                            <CFValueItemAdd
                                                                                customFieldList={customFields}
                                                                                onAdd={(customField) =>
                                                                                    onAddCF(
                                                                                        customField,
                                                                                        currentCustomFields,
                                                                                        add,
                                                                                    )
                                                                                }
                                                                            />
                                                                        )}
                                                                    </>
                                                                }
                                                            />
                                                        )}
                                                    </TabPane>
                                                );
                                            })}
                                    </Tabs>
                                ) : (
                                    <DisplayEmptyData
                                        label={
                                            <>
                                                {i('customField.none')}
                                                {!disabled && (
                                                    <CFValueItemAdd
                                                        customFieldList={customFields}
                                                        onAdd={(customField) =>
                                                            onAddCF(customField, currentCustomFields, add)
                                                        }
                                                    />
                                                )}
                                            </>
                                        }
                                    />
                                )}
                            </THForm.Item>
                        );
                    }}
                </THForm.List>
            </THForm.Item>
        ) : null;
    },
);
